import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { VideoCategoriesDataService, VideoCategoriesColumnsService } from '../../services';
import { DialogVideoCategoryFormComponent } from './video-category-form/video-category-form.component';
import { DialogVideoCategoryDetailsComponent } from './video-category-details/video-category-details.component';
import { VideoCategory, VideoCategoriesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-video-categories',
  templateUrl: './video-categories.component.html',
  styleUrls: ['./video-categories.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class VideoCategoriesComponent implements OnInit, OnDestroy {

  private videoCategoryAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private videoCategoryEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private videoCategoryDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public videoCategoryToDelete: VideoCategory = {};
  public videoCategories: VideoCategory[] = [];
  public videoCategory: VideoCategory = {};
  public selectedVideoCategories: VideoCategory[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private videoCategoriesDataService: VideoCategoriesDataService,
    private videoCategoriesColumnsService: VideoCategoriesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('VideoCategoriesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Video Categories - Management');
    this.primengConfig.ripple = true;
    this.getVideoCategories();
  }

  ngOnDestroy(): void {
    console.log('VideoCategoriesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getVideoCategories(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.videoCategoriesDataService.getVideoCategories(this.parameters).subscribe((response: VideoCategoriesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.videoCategories = response.results;
        } else {
          this.videoCategories = this.videoCategories.concat(response.results);
        }
        if (response.inlineCount > this.videoCategories.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getVideoCategories();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOCATEGORIESNOTLOADED')});
    });
  }

  importVideoCategories(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.videoCategoriesDataService.addVideoCategories(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('VIDEOCATEGORIESCREATED')});
                if (proceed === j) {
                  this.getVideoCategories();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOCATEGORIESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOCATEGORIESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.videoCategoriesDataService.updateVideoCategories(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('VIDEOCATEGORIESUPDATED')});
                if (proceed === j) {
                  this.getVideoCategories();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOCATEGORIESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOCATEGORIESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.videoCategoriesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('videoCategories.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedVideoCategories() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Video Categories?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.videoCategoriesDataService.deleteVideoCategories(this.selectedVideoCategories).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Video Categories Deleted', life: 3000});
            this.getVideoCategories();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Video Categories not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Video Categories not deleted'});
        });
        this.selectedVideoCategories = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.videoCategories, this.columns, 'VideoCategories');
  }

  exportExcel() {
    ToolsService.exportExcel(this.videoCategories, this.columns, 'VideoCategories');
  }

  exportCsv() {
    ToolsService.exportCsv(this.videoCategories, this.columns, 'VideoCategories');
  }

  refresh() {
    this.videoCategoriesDataService.clearCache();
    this.getVideoCategories();
  }

  videoCategoryAdd(): void {
    if (!this.navigation.getParamValue('videoCategories.addInDialog', true)) {
      this.router.navigate(['/video-categories/add']);
      return;
    }
    this.videoCategoryAddDialog = this.dialogService.open(DialogVideoCategoryFormComponent, {
      header: '', // this.translate.instant('NEWVIDEOCATEGORY'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        videoCategory: {id: null},
        videoTypes: this.videoCategoriesColumnsService.videoTypes,
        users: this.videoCategoriesColumnsService.users,
      },
    });

    this.videoCategoryAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'VideoCategory added'});
        this.getVideoCategories();
      }
    });
  }

  videoCategoryEdit(videoCategory: VideoCategory): void {
    if (!this.navigation.getParamValue('videoCategories.editInDialog', true)) {
      this.router.navigate(['/video-categories/edit/' + videoCategory.id]);
      return;
    }
    this.videoCategoryEditDialog = this.dialogService.open(DialogVideoCategoryFormComponent, {
      header: '', // this.translate.instant('EDITVIDEOCATEGORY') + ' ' + videoCategory.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        videoCategory: videoCategory,
        videoTypes: this.videoCategoriesColumnsService.videoTypes,
        users: this.videoCategoriesColumnsService.users,
      },
    });

    this.videoCategoryEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'VideoCategory updated'});
        this.getVideoCategories();
      }
    });
  }

  videoCategoryDetails(videoCategory: VideoCategory): void {
    if (!this.navigation.getParamValue('videoCategories.detailsInDialog', true)) {
      this.router.navigate(['/video-categories/details/' + videoCategory.id]);
      return;
    }
    this.videoCategoryDetailsDialog = this.dialogService.open(DialogVideoCategoryDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        videoCategory: videoCategory,
      }
    });
  }

  videoCategoryDelete(videoCategory: VideoCategory): void {
    this.videoCategoryToDelete = videoCategory;
    this.messageService.clear();
    this.messageService.add({
      key: 'videoCategoryDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + videoCategory.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.videoCategoriesDataService.deleteVideoCategory(this.videoCategoryToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Video Category deleted'});
      this.getVideoCategories();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Video Category not deleted'});
    });
  }

  onDeleteReject(): void {
    this.videoCategoryToDelete = {};
    this.messageService.clear('videoCategoryDelete');
  }

}
